// "My Lists" rail at the top of the Watchlist tab: compact summary cards in a
// horizontal scroller (no list contents), a "+ New List" action in the header
// and a one-line empty state. Placed above the Watchlist so it is discoverable.
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MyListsService, getMyListErrorMessage } from '../../services/myListsService';
import { formatListMeta } from '../../utils/myListItems';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import ListNameModal from './ListNameModal';
import styles from './MyListsSection.module.css';

export function MyListsSectionView({ lists, loading, error, onCreate, onRetry }) {
    const showEmpty = !loading && !error && lists.length === 0;
    return (
        <section className={styles.section} aria-labelledby="my-lists-heading">
            <div className={styles.head}>
                <h2 id="my-lists-heading" className={styles.heading}>My Lists</h2>
                <button type="button" className={styles.headAction} onClick={onCreate}>+ New List</button>
            </div>

            {error && lists.length === 0 && (
                <button type="button" className={styles.errorBox} onClick={onRetry}>
                    Couldn't load your lists. Click to retry.
                </button>
            )}

            {showEmpty && (
                <button type="button" className={styles.empty} onClick={onCreate}>
                    <span className={styles.emptyIcon} aria-hidden="true">+</span>
                    <span className={styles.emptyBody}>
                        <span className={styles.emptyTitle}>Organize what you want to watch.</span>
                        <span className={styles.emptySubtitle}>Create lists for anime, date night, marathons, or anything else.</span>
                    </span>
                    <span className={styles.emptyCta}>Create a List</span>
                </button>
            )}

            {lists.length > 0 && (
                <ul className={styles.rail}>
                    {lists.map((list) => (
                        <li key={list.id} className={styles.railItem}>
                            <Link to={`/lists/${list.id}`} className={styles.card} aria-label={`${list.name}, ${formatListMeta(list)}`}>
                                <span className={styles.stack} aria-hidden="true">
                                    {(list.previewPosters ?? []).slice(0, 3).map((url, index) => (
                                        <img key={`${url}-${index}`} src={url} alt="" className={styles.stackPoster} style={{ left: index * 16, zIndex: 10 - index }} loading="lazy" />
                                    ))}
                                    {!(list.previewPosters ?? []).length && <span className={styles.stackEmpty}>🎬</span>}
                                </span>
                                <span className={styles.cardTitle}>{list.name}</span>
                                <span className={styles.cardMeta}>{formatListMeta(list)}</span>
                            </Link>
                        </li>
                    ))}
                    <li className={styles.railItem}>
                        <button type="button" className={`${styles.card} ${styles.createCard}`} onClick={onCreate}>
                            <span className={styles.emptyIcon} aria-hidden="true">+</span>
                            <span className={styles.createText}>Create New List</span>
                        </button>
                    </li>
                </ul>
            )}

            {loading && lists.length === 0 && <p className={styles.loading}>Loading your lists…</p>}
        </section>
    );
}

export default function MyListsSection({ source = 'watchlist' }) {
    const navigate = useNavigate();
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await MyListsService.list();
            setLists(Array.isArray(data?.lists) ? data.lists : []);
        } catch (err) {
            setError(getMyListErrorMessage(err, "Couldn't load your lists."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function handleCreate({ name, description, visibility }) {
        const data = await MyListsService.create({ name, description, visibility });
        trackEvent(ANALYTICS_EVENTS.MY_LIST_CREATE, { source_screen: source, has_description: !!description, visibility: visibility || 'private' });
        setCreating(false);
        if (data?.list?.id) navigate(`/lists/${data.list.id}`);
    }

    return (
        <>
            <MyListsSectionView lists={lists} loading={loading} error={error} onCreate={() => setCreating(true)} onRetry={load} />
            <ListNameModal isOpen={creating} mode="create" onSubmit={handleCreate} onClose={() => setCreating(false)} />
        </>
    );
}
